import { Radio, Skeleton, Typography } from '@mui/material';
import i18n from 'i18n';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { getUserDocuments } from 'store/user-service/asyncActions';
import {
	getChoosedFile,
	getIsLoading,
	getUserFiles,
	getUserFilesPagination,
} from 'store/user-service/selector';
import { chooseFile } from 'store/user-service/userSlice';

import { NamespacesList } from 'core/enums/common';
import { UserFile } from 'core/types/base';

import { LongTextTooltip } from 'components/shared/LongTextTooltip';
import { SharedModal } from 'components/shared/Modal';

import { ReactComponent as Arrow } from 'assets/icons/arrow-down.svg';
import { ReactComponent as File } from 'assets/icons/document.svg';
import { ReactComponent as Pdf } from 'assets/icons/pdf.svg';

import * as Styled from './styled';

interface FilesModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const FilesModal: FC<FilesModalProps> = ({ isOpen, onClose }) => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);

	const dispatch = useAppDispatch();

	const isLoading = useSelector(getIsLoading);
	const userFiles = useSelector(getUserFiles);
	const choosedFile = useSelector(getChoosedFile);
	const userFilesPagination = useSelector(getUserFilesPagination);

	const [currentFile, setCurrentFile] = useState(choosedFile || null);
	const [uploadingFile, setUploadingFile] = useState(false);

	const processedUserFiles = useMemo(
		() => userFiles?.filter((file) => file.isReadyToUse),
		[userFiles]
	);

	const getFileStatus = (file: UserFile) => {
		switch (file.isReadyToUse) {
			case true:
				return (
					<Typography
						sx={{
							color: 'rgb(1, 200, 96)',
						}}
					>
						{profileTranslate('text.ready')}
					</Typography>
				);
			case false:
				return (
					<Typography
						sx={{
							color: 'rgb(241, 151, 2)',
						}}
					>
						{profileTranslate('text.notReady')}
					</Typography>
				);
			case null:
				return (
					<Typography
						sx={{
							color: 'rgb(71, 134, 255)',
						}}
					>
						{profileTranslate('text.processing')}
					</Typography>
				);
			default:
				return null;
		}
	};

	const handleChooseFile = (file: UserFile | null) => {
		dispatch(chooseFile(file));
	};

	const getPageNumber = useMemo(() => {
		if (userFilesPagination) {
			return Math.ceil(
				userFilesPagination?.totalRecordsFound / userFilesPagination?.recordsPerPage
			);
		}

		return 1;
	}, [userFilesPagination]);

	const handleChangePage = async (_event: ChangeEvent<unknown>, value: number) => {
		if (value !== userFilesPagination?.currentPageNumber) {
			await dispatch(getUserDocuments({ currentPageNumber: value, recordsPerPage: 4 }));
		}
	};

	useEffect(() => {
		isOpen && dispatch(getUserDocuments({ currentPageNumber: 1, recordsPerPage: 4 }));
	}, [isOpen]);

	const handleChangeRadioButton = (event: any) => {
		const currentFile = processedUserFiles?.find((file) => file.name === event.target.value);
		setCurrentFile(currentFile || null);
	};

	const handleUploadUserFile = async () => {
		setUploadingFile(true);
		await handleChooseFile(currentFile || null);
		setUploadingFile(false);
		setTimeout(() => {
			onClose();
		}, 700);
	};

	const handleClose = () => {
		onClose();
		if (choosedFile) {
			setCurrentFile(choosedFile);
		} else {
			setCurrentFile(null);
		}
	};

	const handleCancel = () => {
		setCurrentFile(null);
		handleChooseFile(null);
		onClose();
	};

	return (
		<SharedModal isOpen={isOpen} handleClose={handleClose}>
			<Styled.ModalContent
				className="rtl-able"
				sx={{
					width: { xs: '100%', sm: '600px' },
				}}
			>
				<Styled.ModalTitle>{modalTranslate('filesModal.title')}</Styled.ModalTitle>
				{processedUserFiles?.length === 0 && !isLoading ? (
					<Styled.EmptyState>
						<File />
						<Typography variant="h3">{profileTranslate('text.emptyFileState')}</Typography>
						<Typography>{modalTranslate('filesModal.filesEmptyState')}</Typography>
					</Styled.EmptyState>
				) : (
					<Styled.Pagination>
						<Styled.PaginationButton
							onClick={(e: any) =>
								handleChangePage(e, (userFilesPagination?.currentPageNumber || 1) - 1)
							}
							sx={{
								transform: i18n.language === 'ar' ? 'rotate(-90deg)' : 'rotate(90deg)',
								opacity: userFilesPagination?.currentPageNumber === 1 ? 0 : 1,
								pointerEvents: userFilesPagination?.currentPageNumber === 1 ? 'none' : 'auto',
							}}
						>
							<Arrow />
						</Styled.PaginationButton>
						<Styled.ModalFiles className="rtl-able">
							{isLoading ? (
								<>
									{[...Array(4)].map((_e, i) => (
										<Styled.ModalFile key={i}>
											<Skeleton
												variant="circular"
												width={24}
												height={24}
												sx={{ margin: '9px' }}
												animation="wave"
											/>
											<Styled.ModalFileContent>
												<Skeleton variant="rounded" width={24} height={24} animation="wave" />
												<Styled.ModalFileDescription>
													<Skeleton variant="rounded" width={158} height={15} animation="wave" />
													<Skeleton variant="rounded" width={85} height={15} animation="wave" />
												</Styled.ModalFileDescription>
											</Styled.ModalFileContent>
										</Styled.ModalFile>
									))}
								</>
							) : (
								processedUserFiles?.map((file) => (
									<Styled.ModalFile key={file.name}>
										<Radio
											value={file.name}
											onChange={handleChangeRadioButton}
											checked={currentFile?.name === file.name}
											disabled={choosedFile?.name === file.name}
										/>
										<Styled.ModalFileContent>
											<Pdf></Pdf>
											<Styled.ModalFileDescription>
												<LongTextTooltip
													title={file.name || 'File name'}
													sx={{ maxWidth: '200px' }}
													variant="h3"
												/>
												{getFileStatus(file)}
											</Styled.ModalFileDescription>
										</Styled.ModalFileContent>
									</Styled.ModalFile>
								))
							)}
						</Styled.ModalFiles>
						<Styled.PaginationButton
							disabled={userFilesPagination?.currentPageNumber === getPageNumber}
							onClick={(e: any) =>
								handleChangePage(e, (userFilesPagination?.currentPageNumber || 1) + 1)
							}
							sx={{ transform: i18n.language === 'ar' ? 'rotate(90deg)' : 'rotate(-90deg)' }}
						>
							<Arrow />
						</Styled.PaginationButton>
					</Styled.Pagination>
				)}

				<Styled.ModalButtons>
					<Styled.CancelButton onClick={handleCancel}>
						{modalTranslate('filesModal.cancel')}
					</Styled.CancelButton>
					<Styled.ConfirmButton
						onClick={handleUploadUserFile}
						disabled={uploadingFile || choosedFile?.name === currentFile?.name || !currentFile}
					>
						{uploadingFile ? (
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#91B6FF"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						) : (
							modalTranslate('filesModal.confirm')
						)}
					</Styled.ConfirmButton>
				</Styled.ModalButtons>
			</Styled.ModalContent>
		</SharedModal>
	);
};
